import { useEffect } from 'react';
import LoyaltyAccessor from '@/components/account/loyaltyAccessor';
import { BankedCurrency } from '@/components/account/rewards/banked-currency';
import { RewardsFooter } from '@/components/account/rewards/footer';
import LoyaltyState from '@/components/account/rewards/loyaltyState';
import { MyOffers } from '@/components/account/rewards/my-offers';
import { MyRewards } from '@/components/account/rewards/my-rewards';
import {
  StyledSecondarySection,
  StyledTopLevelLoyaltyContainer,
} from '@/components/account/styles';
import Layout from '@/components/app/layout/index';
import { ConnectionErrorState } from '@/components/connectionError';
import { RestrictedAccountRoute } from '@/components/restrict';
import {
  FETCH_POINTS_HISTORY_ERROR_COPY,
  FETCH_REDEMPTIONS_ERROR_COPY,
  FETCH_REWARDS_ERROR_COPY,
  LOYALTY_ACCESSOR_TYPES,
  LOYALTY_FEATURES,
} from '@/constants/loyalty';
import { useSelector } from '@/redux';
import globalActions from '@/redux/global/actions';
import { useAccountDispatch } from '@/redux/me/use-account';
import { userLoggedIn } from '@/utils/auth';

const CONNECTION_ERROR_TEST_ID = 'rewards-connection-error';

/** Toasts this page's own loyalty fetches raise — the same outage the connection state shows. */
const REWARDS_FETCH_TOASTS: ReadonlySet<string> = new Set([
  FETCH_REWARDS_ERROR_COPY,
  FETCH_POINTS_HISTORY_ERROR_COPY,
  FETCH_REDEMPTIONS_ERROR_COPY,
]);

const RewardsPage = () => {
  const {
    dispatch,
    fetchRewardsHistory,
    fetchRedeemables,
    fetchOffers,
    fetchLoyaltyState,
    fetchRedemptions,
  } = useAccountDispatch();

  const loyaltyState = useSelector((state) => state.app.me.loyaltyState);
  const loyaltyConnectionError = useSelector((state) => state.app.me.loyaltyConnectionError);
  const toast = useSelector((state) => state.app.global.toast);

  // While this page shows the connection-error state, the dismiss-only loyalty toasts for the
  // same outage are a second, differently-worded notice on top of it (TEAM-14780 QA: "Show
  // one"). Drop them here rather than suppressing them in the sagas — those sagas also run on
  // pages with no connection UI, where the toast is the only signal. Re-checked whenever the
  // toast changes, so it holds whichever of the parallel fetches fails first.
  useEffect(() => {
    if (loyaltyConnectionError && toast?.display && REWARDS_FETCH_TOASTS.has(toast.message)) {
      dispatch(globalActions.dismissToast());
    }
  }, [loyaltyConnectionError, toast?.display, toast?.message, dispatch]);

  const loadRewards = () => {
    dispatch(fetchRewardsHistory());
    dispatch(fetchRedeemables());
    dispatch(fetchOffers());
    dispatch(fetchLoyaltyState());
    dispatch(fetchRedemptions());
  };

  useEffect(() => {
    if (userLoggedIn()) {
      loadRewards();
    }
  }, []);

  // `loyaltyState` is hydrated on every page load, so it is almost always already
  // populated by the time a diner opens this page. The existing outer gate therefore
  // never reached its `loyaltyConnectionError` arm in normal browsing, and a failed
  // refetch left the last-known-good points on screen with no signal at all
  // (TEAM-14780 QA, 2026-09-14).
  //
  // The fix is the BANNER INSIDE the `loyaltyState` branch, not the outer gate — that
  // gate is unchanged and still handles only the cold case. Stale points are
  // informational rather than transactional, so when there is content to keep the
  // failure is flagged above it and the content stands (TEAM-14742 AC4); only a cold
  // failure with nothing to show falls back to the full panel.
  return (
    <RestrictedAccountRoute>
      <Layout>
        {loyaltyState ? (
          <StyledTopLevelLoyaltyContainer>
            {loyaltyConnectionError && (
              <ConnectionErrorState
                variant="banner"
                data-testid={CONNECTION_ERROR_TEST_ID}
                onRetry={loadRewards}
              />
            )}
            <LoyaltyAccessor
              checkType={LOYALTY_ACCESSOR_TYPES.FEATURE}
              checkName={LOYALTY_FEATURES.SHOW_LOYALTY_STATE}
              component={<LoyaltyState />}
            />
            <StyledSecondarySection>
              <LoyaltyAccessor
                checkType={LOYALTY_ACCESSOR_TYPES.FEATURE}
                checkName={LOYALTY_FEATURES.INDEX_REDEEMABLES}
                component={<MyRewards />}
              />
              <BankedCurrency />
              <LoyaltyAccessor
                checkType={LOYALTY_ACCESSOR_TYPES.FEATURE}
                checkName={LOYALTY_FEATURES.INDEX_MY_REWARDS}
                component={<MyOffers />}
              />
            </StyledSecondarySection>
            <RewardsFooter />
          </StyledTopLevelLoyaltyContainer>
        ) : loyaltyConnectionError ? (
          <ConnectionErrorState data-testid={CONNECTION_ERROR_TEST_ID} onRetry={loadRewards} />
        ) : null}
      </Layout>
    </RestrictedAccountRoute>
  );
};

export default RewardsPage;
